/* eslint-disable max-len */

import { Burden } from '@deqo/foundation';
import { waitFor } from '../../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { PodTransactionData } from '../../../api/endpoints/pods';
import { ClientManager, Communicator, ObjectManager } from '../../../concepts/objects';
import { RecordSetManager } from '../../../concepts/objects/RecordSetManager';
import { TopicManager } from '../../../concepts/objects/TopicManager';
import { DefaultPackageHost, PackageType, Resolver } from '../../Container';
import { Packages } from '../../Packages';
import { PassportModel } from '../../PassportModel';
import { createScope } from '../../Scope';
import { PopupManager } from '../../popups/PopupManager';
import { CorePackage } from '../CorePackage';
import { EditorOptions } from '../EditorOptions';
import { EditorPodSession } from '../EditorPodSession';
import { MetadataProvider } from '../MetadataProvider';
import { StaticEditorPodSession } from '../StaticEditorPodSession';
import { HistoryPackage } from '../history/HistoryPackage';
import { ToolingPackage } from '../tooling/ToolingPackage';
import { DisabledPopupManager } from './DisabledPopupManager';
import { ReplayController } from './ReplayController';
import { ReplayCustomPackage } from './ReplayCustomPackage';

export const ReplayScope = createScope<{ boardId: string, elmtId?: string }>({
    async refresh({ boardId, elmtId }, c, progress) {

        c.get;

        const ts = performance.now();
        const trash = [] as Burden[];

        const api = c.get(ApiClient);
        const resolver = c.get(Resolver);

        const boardData = await api.exec(v2.boards.info(boardId as string));
        const permissionsData = await api.exec(v2.me.permissions(['board', boardId as string]));

        const { podId } = boardData;
        const transactions = [] as PodTransactionData[];
        const snapshot = await api.exec(v2.pods.snapshots(podId, 'initial'));
        const { count } = await api.exec(v2.pods.transactionStats(podId));
        const batch = 2500;

        for (let i = 0; i < count; i += batch) {
            const block = await api.exec(v2.pods.transactions(podId, i, batch));
            transactions.push(...block);
            progress(i / count);
        }

        progress(1);

        const te = performance.now() - ts;
        if (te < 500) {
            await waitFor(500 - te);
        }

        const dataProvider = new MetadataProvider(api, boardData);
        const podSession = new StaticEditorPodSession({ time: 0, ...snapshot });
        const ctrl = ReplayController.load(boardData.id, snapshot, transactions);

        if (elmtId) {
            ctrl.setPositionToViewElement(elmtId);
        }

        const objects = new ObjectManager(resolver, ctrl);
        const clients = new ClientManager(podSession as any);
        const comms = new Communicator(podSession as any, clients);
        const topics = new TopicManager(podSession as any);
        const recordSets = new RecordSetManager(podSession as any);

        c.bind(EditorPodSession).toConstantValue(podSession);
        c.bind(ClientManager).toConstantValue(clients);
        c.bind(Communicator).toConstantValue(comms);
        c.bind(TopicManager).toConstantValue(topics);
        c.bind(RecordSetManager).toConstantValue(recordSets);
        c.bind(MetadataProvider).toConstantValue(dataProvider);
        c.bind(ObjectManager).toConstantValue(objects);
        c.bind(EditorOptions).toConstantValue({ elementId: elmtId, kiosk: true });
        c.bind(ReplayController).toConstantValue(ctrl);
        c.bind(PassportModel).toConstantValue(PassportModel.create(permissionsData));

        c.unbind(PopupManager);
        c.bind(PopupManager).to(DisabledPopupManager).inSingletonScope();

        trash.push(objects, clients, comms);

        const host = new DefaultPackageHost(c);

        const basePackages: PackageType[] = [
            CorePackage,
            HistoryPackage,
            ToolingPackage,
            ReplayCustomPackage,
        ];

        const allPackages = basePackages.concat(Packages);
        allPackages.forEach(t => (new t()).install(host));

        await host.go();
        trash.push(host);

        return trash;
    }
});
